import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Search, Check, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { api } from '../lib/api';

const wrap = { position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(6,12,26,0.78)', backdropFilter: 'blur(14px)', display: 'grid', placeItems: 'center', padding: 14 };
const card = (w) => ({ width: '100%', maxWidth: w, maxHeight: '90vh', overflowY: 'auto', borderRadius: 20, padding: 20, background: 'linear-gradient(180deg, rgba(20,29,66,0.92) 0%, rgba(14,22,44,0.98) 100%)', border: '1px solid var(--bd)' });
const STATUS = {
  ok: ['OK', '#34D399', 'rgba(52,211,153,0.12)'],
  error: ['Erreur', '#F87171', 'rgba(248,113,113,0.12)'],
  unsure: ['Potentiellement erreur', '#F5C56A', 'rgba(245,197,106,0.12)'],
  copied: ['Concept copié', '#F5C56A', 'rgba(245,197,106,0.12)'],
  fixed: ['✓ Corrigé', '#5BBEFC', 'rgba(91,190,252,0.10)'],
};
const small = { padding: '6px 11px', borderRadius: 9, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, background: 'transparent', border: '1px solid var(--bd)', color: 'var(--t2)' };

const Head = ({ icon: Icon, title, onClose }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
    <Icon size={18} color="#F5C56A" />
    <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: 'var(--t1)' }}>{title}</div>
    <button data-testid="dialog-close" onClick={onClose} style={{ ...small, padding: 7 }}><X size={14} /></button>
  </div>
);

const ORDER = { error: 0, copied: 1, unsure: 2, fixed: 3, ok: 4 };
const sortRows = (rows) => [...rows].sort((a, b) => ORDER[a.status] - ORDER[b.status]);

// Badge « Erreur / Potentiellement erreur » sur une vidéo (résultat de la dernière vérification).
export function PubCheckBadge({ check }) {
  if (!check || check.status === 'ok') return null;
  const [label, color] = STATUS[check.status] || STATUS.unsure;
  return <span data-testid="pub-check-badge" title={check.why || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: `${color}22`, border: `1px solid ${color}66`, color, fontFamily: 'Sora', fontWeight: 800, fontSize: '0.66rem' }}>⚠️ Titre : {label}</span>;
}

// Vérifie (IA) que chaque titre/description de publication est sur la bonne vidéo, et corrige.
export function PublicationCheckDialog({ project, initialRows, onClose, onSaved }) {
  const [rows, setRows] = useState(initialRows ? sortRows(initialRows) : null);
  const [busy, setBusy] = useState(false);
  const run = () => {
    setRows(null);
    api.post(`/projects/${project.id}/publication-check`, {}, { timeout: 120000 })
      .then((r) => setRows(sortRows(r.data.rows))).catch((e) => { toast.error(e?.response?.data?.detail || 'Erreur'); onClose(); });
  };
  useEffect(() => { if (!initialRows) run(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Corrige sans relancer l'IA : les lignes passent en « Corrigé » directement.
  const fix = async (fixRows) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/publication-fix`, { moves: fixRows.map(moveOf) });
      toast.success(`${r.data.fixed} correction(s) appliquée(s) ✓`);
      const done = new Set(fixRows.map((x) => x.video_id));
      setRows((rs) => rs.map((x) => (done.has(x.video_id) ? { ...x, status: 'fixed' } : x)));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };
  const moveOf = (r) => (r.status === 'copied' ? { from: r.video_id, to: '' } : r.suggested_video_id ? { from: r.video_id, to: r.suggested_video_id } : null);
  const fixable = (rows || []).filter((r) => ['error', 'unsure', 'copied'].includes(r.status) && moveOf(r));
  const nErr = (rows || []).filter((r) => !['ok', 'fixed'].includes(r.status)).length;
  return createPortal(
    <div data-testid="pub-check-dialog" onClick={onClose} style={wrap}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={card(760)}>
        <Head icon={Search} title="Vérifier les titres et descriptions" onClose={onClose} />
        {!rows ? (
          <div style={{ padding: 30, textAlign: 'center', fontFamily: 'Sora', color: 'var(--t2)' }}>L&apos;IA compare chaque publication au concept de sa vidéo…</div>
        ) : (
          <>
            <div data-testid="pub-check-summary" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: nErr ? '#F87171' : '#34D399' }}>
              {nErr ? <AlertTriangle size={15} /> : <Check size={15} />}
              <span style={{ flex: 1 }}>{rows.length === 0 ? 'Aucune publication à vérifier' : nErr ? `${nErr} problème(s) sur ${rows.length}` : `Tout est bon (${rows.length} publications)`}</span>
              <button data-testid="pub-check-rerun" disabled={busy} onClick={run} style={small}>Revérifier</button>
              {fixable.length > 0 && <button data-testid="pub-check-fix-all" disabled={busy} onClick={() => fix(fixable)} className="asa-btn asa-btn-primary" style={{ padding: '8px 14px' }}>{busy ? 'Correction…' : `Tout corriger (${fixable.length})`}</button>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((r) => {
                const [label, color, bg] = STATUS[r.status];
                const mv = moveOf(r);
                return (
                  <div key={r.video_id} data-testid={`pub-check-row-${r.video_id}`} style={{ padding: '10px 12px', borderRadius: 12, background: bg, border: `1px solid ${color}55` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 999, background: `${color}22`, color, fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5 }}>{label}</span>
                      <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, color: 'var(--t1)' }}><span style={{ color: 'var(--t3)', fontWeight: 600 }}>Vidéo :</span> {r.video_title}</span>
                      {r.due_date && <span style={{ fontFamily: 'Sora', fontSize: 11, color: 'var(--t3)' }}>· {r.due_date}</span>}
                      <span style={{ flex: 1 }} />
                      {!['ok', 'fixed'].includes(r.status) && mv && (
                        <button data-testid={`pub-check-fix-${r.video_id}`} disabled={busy} onClick={() => fix([r])} style={{ ...small, color: '#5BBEFC', borderColor: 'rgba(91,190,252,0.4)' }}>
                          {r.status === 'copied' ? 'Effacer (Sans titre)' : `Déplacer sur « ${r.suggested_video_title} »`}
                        </button>
                      )}
                    </div>
                    <div style={{ marginTop: 6, fontFamily: 'Lexend', fontSize: 12, color: 'var(--t2)' }}><span style={{ color: 'var(--t3)' }}>Titre publié :</span> <b>{r.publication_title || 'Sans titre'}</b>{r.publication_description ? ` — ${r.publication_description.slice(0, 140)}…` : ''}</div>
                    {r.why && !['ok', 'fixed'].includes(r.status) && <div style={{ marginTop: 4, fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)' }}>{r.why}</div>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Couvertures de réel : activation + lien Drive global du projet.
export function CoversDialog({ project, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(!!project.covers_enabled);
  const [url, setUrl] = useState(project.covers_link || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/covers`, { enabled, url });
      toast.success(enabled ? 'Couvertures activées ✓' : 'Couvertures désactivées');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); setBusy(false); }
  };
  return createPortal(
    <div data-testid="covers-dialog" onClick={onClose} style={wrap}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={card(460)}>
        <Head icon={ImageIcon} title="Couvertures de réel" onClose={onClose} />
        <button data-testid="covers-toggle" onClick={() => setEnabled((x) => !x)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', background: enabled ? 'rgba(52,211,153,0.10)' : 'rgba(255,255,255,0.03)', border: `1px solid ${enabled ? 'rgba(52,211,153,0.45)' : 'var(--bd)'}`, color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }}>
          <span style={{ width: 38, height: 22, borderRadius: 999, background: enabled ? '#34D399' : 'rgba(255,255,255,0.15)', position: 'relative', transition: 'background-color .2s' }}>
            <span style={{ position: 'absolute', top: 3, left: enabled ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
          </span>
          {enabled ? 'Ce projet a des couvertures' : 'Pas de couvertures'}
        </button>
        {enabled && (
          <input data-testid="covers-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Lien Drive des couvertures" style={{ width: '100%', marginTop: 12, padding: '11px 12px', borderRadius: 12, background: 'var(--sf2)', border: '1.5px solid var(--bd)', color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 13 }} />
        )}
        <div style={{ marginTop: 10, fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)' }}>Le lien apparaît sous le lien de la vidéo dans chaque tâche « 📱 Poster ». Modifiable vidéo par vidéo depuis la tâche.</div>
        <button data-testid="covers-save" disabled={busy} onClick={save} className="asa-btn asa-btn-primary" style={{ width: '100%', marginTop: 14 }}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
    </div>,
    document.body,
  );
}
